import { ROUTES } from "@/constants/Routes";
import { useAbility } from "@/hooks/useAbility";
import { canCheck } from "@/lib/casl/canCheck";
import type { AbilityDescriptor } from "@/lib/casl/types";
import { useUserStore } from "@/store/useUserStore";
import type { RoutePath } from "@/types/RoutePath";
import { useRouter } from "next/router";
import { useEffect } from "react";

const AGAT_ALLOWED_ROUTES: string[] = [
  ROUTES.HOME,
  ROUTES.AUTH.ACCOUNT_ACTIVATION,
  ROUTES.AUTH.PROFILE,
];

const ROUTE_ABILITIES = {
  // AIA
  [ROUTES.AIA.CONSULT]: { action: "read", subject: "AIA" },
  [ROUTES.AIA.REGISTER]: { action: ["read", "create"], subject: "AIA" },
  [ROUTES.AIA.EDIT]: { action: ["read", "update"], subject: "AIA" },
  // EPS
  [ROUTES.EPS.CONSULT]: { action: "read", subject: "EPS" },
  [ROUTES.EPS.REGISTER]: { action: ["read", "create"], subject: "EPS" },
  [ROUTES.EPS.EDIT]: { action: ["read", "update"], subject: "EPS" },
  // Principles
  [ROUTES.PRINCIPLE.CONSULT]: { action: "read", subject: "Principle" },
  [ROUTES.PRINCIPLE.REGISTER]: {
    action: ["read", "create"],
    subject: "Principle",
  },
  [ROUTES.PRINCIPLE.EDIT]: { action: ["read", "update"], subject: "Principle" },
  // Users
  [ROUTES.USER.CONSULT]: { action: "read", subject: "User" },
  [ROUTES.USER.REGISTER]: { action: ["read", "create"], subject: "User" },
  [ROUTES.USER.EDIT]: { action: ["read", "update"], subject: "User" },
  // Organizations
  [ROUTES.ORGANIZATION.CONSULT]: { action: "read", subject: "Organization" },
  // Applications
  [ROUTES.APPLICATION.CONSULT]: { action: "read", subject: "Application" },
  [ROUTES.APPLICATION.EVALUATE]: {
    action: ["read", "evaluate"],
    subject: "Application",
  },
  [ROUTES.APPLICATION.DELIBERATE]: {
    action: ["read", "deliberate"],
    subject: "Application",
  },
  // Questionnaires
  [ROUTES.QUESTIONNAIRE.REGISTER.AIA]: {
    action: ["read", "create"],
    subject: "AIAQuestionnaire",
  },
  [ROUTES.QUESTIONNAIRE.ANSWER.AIA]: {
    action: ["cancel", "answer"],
    subject: "AIAQuestionnaire",
  },
  [ROUTES.QUESTIONNAIRE.REGISTER.EPS]: {
    action: ["read", "create"],
    subject: "EPSQuestionnaire",
  },
  [ROUTES.QUESTIONNAIRE.ANSWER.EPS]: {
    action: ["cancel", "answer"],
    subject: "EPSQuestionnaire",
  },
  // Sections
  [ROUTES.SECTION.CONSULT]: { action: "read", subject: "Section" },
  [ROUTES.SECTION.REGISTER]: { action: ["read", "create"], subject: "Section" },
  [ROUTES.SECTION.EDIT]: { action: ["read", "update"], subject: "Section" },
  // Questions
  [ROUTES.QUESTION.CONSULT]: { action: "read", subject: "Question" },
  [ROUTES.QUESTION.REGISTER]: {
    action: ["read", "create"],
    subject: "Question",
  },
  [ROUTES.QUESTION.EDIT]: { action: ["read", "update"], subject: "Question" },
} satisfies Record<RoutePath, AbilityDescriptor>;

type RouteGuardProps = {
  children: React.ReactNode;
};

function canAccessRoute(ability: ReturnType<typeof useAbility>, route: string) {
  if (!(route in ROUTE_ABILITIES)) return true;

  const descriptor = ROUTE_ABILITIES[
    route as keyof typeof ROUTE_ABILITIES
  ] as AbilityDescriptor;

  return canCheck(ability, descriptor);
}

export function RouteGuard({ children }: RouteGuardProps) {
  const router = useRouter();
  const role = useUserStore((s) => s.role);
  const attributions = useUserStore((s) => s.attributions);
  const status = useUserStore((s) => s.status);
  const ability = useAbility();

  useEffect(() => {
    if (status === "agat") {
      if (!AGAT_ALLOWED_ROUTES.includes(router.pathname)) {
        router.push(ROUTES.AUTH.ACCOUNT_ACTIVATION);
      }
      return;
    }

    // com ?token= é a ativação pelo link do e-mail, que vale mesmo com outra sessão aberta no navegador
    if (
      status !== null &&
      router.pathname === ROUTES.AUTH.ACCOUNT_ACTIVATION &&
      !router.query.token
    ) {
      router.push(ROUTES.HOME);
      return;
    }

    const canAccess = canAccessRoute(ability, router.pathname);

    if (!canAccess) {
      router.push(ROUTES.HOME);
    }
  }, [role, attributions, status, ability, router]);

  return <>{children}</>;
}
